import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Typography,
  Stack,
  Box
} from "@mui/material";
import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ProjectContext } from "../../contexts/ProjectContext";
import { AuthContext } from "../../contexts/authContext";
import { USER_TYPES } from "../../constants/userTypes";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { ElementData } from "../../data/elementData";

export const NewSimpleNodeDialog = ({ open, nodetype, handleClose }) => {
  const [newElement, setNewElement] = useState(ElementData);
  const [error, setError] = useState(null);
  const nameFieldRef = useRef(null);
  const { addNode, elements } = useContext(ProjectContext);
  const { userProfile } = useContext(AuthContext);

  // Focus effect with cleanup
  useEffect(() => {
    if (open && nameFieldRef.current) {
      const timer = setTimeout(() => {
        nameFieldRef.current?.focus();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setNewElement(ElementData);
    }
  }, [open]);

  const handleAdd = useCallback(() => {
    if (!newElement.name?.trim()) {
      return;
    }

    // Check if FREE user has reached node limit
    const isFreeUser = userProfile?.type === USER_TYPES.FREE;
    const nodeCount = elements?.filter(el => el.group === "nodes")?.length || 0;
    const maxNodesForFree = 30;
    
    if (isFreeUser && nodeCount >= maxNodesForFree) {
      setError(`Free users can add up to ${maxNodesForFree} nodes. Please upgrade to add more nodes.`);
      return;
    }

    addNode(newElement, nodetype);
    handleClose();
  }, [newElement, nodetype, addNode, handleClose, userProfile, elements]);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const handleCloseDialog = useCallback((event, reason) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      handleClose();
    }
  }, [handleClose]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewElement(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && newElement.name?.trim()) {
      handleAdd();
    }
  }, [newElement.name, handleAdd]);

  const nameLength = newElement?.name?.length || 0;
  const isNameValid = newElement.name?.trim();

  const paperProps = useMemo(() => ({
    sx: {
      borderRadius: 3,
      overflow: 'hidden',
      boxShadow: '0px 12px 40px rgba(15, 23, 42, 0.18)'
    }
  }), []);

  return (
    <>
      <Dialog 
        open={open} 
        maxWidth="sm" 
        fullWidth 
        onClose={handleCloseDialog}
        TransitionProps={{ onEntered: () => nameFieldRef.current?.focus() }}
        PaperProps={paperProps}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2.5,
            pr: 2,
            pl: 3
          }}
        >
          <Box>
            <Typography variant="h6" component="div">
              Add Node
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a quick node by supplying its basic information.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close add node dialog"
            onClick={handleClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          dividers
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            bgcolor: 'background.default'
          }}
        >
          <Stack spacing={2}>
            <TextField 
              size="small" 
              required 
              name="name" 
              label="Name" 
              type="text" 
              fullWidth 
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              inputRef={nameFieldRef}
              inputProps={{ maxLength: 100 }}
              helperText={`${nameLength}/100 characters`}
              error={nameLength === 100}
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            bgcolor: 'background.paper',
            justifyContent: 'flex-end',
            gap: 1.5
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            startIcon={<CloseIcon fontSize="small" />}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAdd} 
            variant="contained" 
            color="success" 
            startIcon={<AddIcon />}
            disabled={!isNameValid}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};